/**
 * Die Inhalte der Ballonreise: Stationen und Scherzfragen.
 *
 * Beides steht im Code und nicht in der Datenbank. Es aendert sich nie von
 * selbst, und eine Tabelle dafuer waere nur eine zusaetzliche Abfrage bei
 * jedem Aufruf. Neue Witze oder Stationen kommen per Deploy dazu.
 */

export interface Station {
  name: string
  land: string
  /** Ein Satz, den ein Achtjaehriges spannend findet und behalten kann. */
  info: string
  /** Position auf der gezeichneten Route, 0 bis 100. */
  x: number
  y: number
}

/**
 * Die Reiseroute.
 *
 * Bewusst eine Reise um die Welt mit echten Orten statt einer Fantasiekarte:
 * Die Kinder nehmen nebenbei etwas mit, und "wir sind jetzt in Kairo" gibt
 * beim Abendessen mehr her als "wir sind auf Feld 47".
 *
 * Die Koordinaten beschreiben keine echte Weltkarte, sondern eine geschwungene
 * Linie von links nach rechts. Ein Kartenumriss waere auf einem Tablet zu
 * klein, um die Stationen auseinanderzuhalten.
 */
export const STATIONEN: Station[] = [
  { name: 'Bad Essen', land: 'Zuhause', info: 'Hier startet eure Reise. 16 Stationen liegen vor euch.', x: 4, y: 60 },
  { name: 'Hamburg', land: 'Deutschland', info: 'Im Hamburger Hafen passen 8000 Schiffe pro Jahr durch die Schleusen.', x: 11, y: 48 },
  { name: 'Kopenhagen', land: 'Dänemark', info: 'Hier fahren mehr Fahrräder als Autos durch die Stadt.', x: 18, y: 36 },
  { name: 'London', land: 'England', info: 'Der Big Ben ist eigentlich die Glocke, nicht der Turm.', x: 25, y: 44 },
  { name: 'Paris', land: 'Frankreich', info: 'Der Eiffelturm wird im Sommer 15 Zentimeter größer, weil das Eisen sich ausdehnt.', x: 32, y: 54 },
  { name: 'Rom', land: 'Italien', info: 'Ins Kolosseum passten 50.000 Menschen, fast so viele wie in ein großes Fußballstadion.', x: 39, y: 64 },
  { name: 'Kairo', land: 'Ägypten', info: 'Die große Pyramide ist über 4500 Jahre alt und war 3800 Jahre lang das höchste Bauwerk der Welt.', x: 46, y: 56 },
  { name: 'Nairobi', land: 'Kenia', info: 'Mitten in der Stadt liegt ein Nationalpark mit Giraffen und Nashörnern.', x: 52, y: 68 },
  { name: 'Dubai', land: 'Emirate', info: 'Der Burj Khalifa ist 828 Meter hoch. Das sind etwa 163 Giraffen übereinander.', x: 58, y: 56 },
  { name: 'Neu-Delhi', land: 'Indien', info: 'In Indien leben mehr Menschen als in ganz Europa.', x: 64, y: 62 },
  { name: 'Peking', land: 'China', info: 'Die Chinesische Mauer ist über 20.000 Kilometer lang.', x: 71, y: 48 },
  { name: 'Tokio', land: 'Japan', info: 'An der größten Kreuzung überqueren bei jeder Grünphase bis zu 3000 Menschen die Straße.', x: 78, y: 54 },
  { name: 'Sydney', land: 'Australien', info: 'Das Dach der Oper besteht aus über einer Million weißer Kacheln.', x: 84, y: 66 },
  { name: 'Honolulu', land: 'Hawaii', info: 'Hawaii besteht aus Vulkanen, die aus dem Meer herausgewachsen sind.', x: 89, y: 52 },
  { name: 'San Francisco', land: 'USA', info: 'Die Golden Gate Bridge wird ständig gestrichen. Wenn sie fertig ist, fängt man wieder von vorn an.', x: 94, y: 38 },
  { name: 'Der Mond', land: 'Weltall', info: 'Geschafft! Ihr habt die ganze Welt umrundet und seid weitergeflogen.', x: 97, y: 24 },
]

/** Wie viele geschaffte Runden eine Etappe zwischen zwei Stationen kostet. */
export const RUNDEN_PRO_ETAPPE = 6

export interface Reisestand {
  /** Index der zuletzt erreichten Station. */
  stationIndex: number
  station: Station
  /** Die nächste Station, oder null am Ziel. */
  naechste: Station | null
  /** Fortschritt zur nächsten Station, 0 bis 1. */
  anteil: number
  /** Position des Ballons auf der gezeichneten Route. */
  x: number
  y: number
  /** Wie viele geschaffte Runden bis zur nächsten Station fehlen. */
  nochRunden: number
  amZiel: boolean
}

/**
 * Wo der Ballon gerade steht.
 *
 * Reine Rechnung aus einer einzigen Zahl: der Summe aller geschafften
 * Tagesrunden im Raum. Der Server muss deshalb nichts über die Reise wissen,
 * und eine neue Station dazuzunehmen ist eine Zeile in STATIONEN, kein
 * Datenbankeingriff.
 */
export function reiseStand(rundenGesamt: number): Reisestand {
  const gesamt = Math.max(0, Math.floor(rundenGesamt))
  const rohIndex = Math.floor(gesamt / RUNDEN_PRO_ETAPPE)
  const stationIndex = Math.min(rohIndex, STATIONEN.length - 1)
  const amZiel = rohIndex >= STATIONEN.length - 1

  const station = STATIONEN[stationIndex]!
  const naechste = amZiel ? null : STATIONEN[stationIndex + 1]!

  const anteil = amZiel ? 1 : (gesamt % RUNDEN_PRO_ETAPPE) / RUNDEN_PRO_ETAPPE

  return {
    stationIndex,
    station,
    naechste,
    anteil,
    x: naechste ? station.x + (naechste.x - station.x) * anteil : station.x,
    y: naechste ? station.y + (naechste.y - station.y) * anteil : station.y,
    nochRunden: amZiel ? 0 : RUNDEN_PRO_ETAPPE - (gesamt % RUNDEN_PRO_ETAPPE),
    amZiel,
  }
}
